import { useCallback } from 'react';

import { FeatureToggle } from '@fly/shared';
import { useGetFeatureToggleQuery } from '@fly/store';
import log from 'loglevel';

const LOCAL_STORAGE_KEY = 'featuresToggle';

// Expose window.setFeature for per-browser feature flag testing (including production).
// This only affects the current browser via localStorage — not a server-side toggle.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).setFeature = (key: string, value: boolean = true) => {
    if (!Object.values(FeatureToggle).includes(key as FeatureToggle)) {
      log.warn('Invalid feature key');
      return;
    }

    try {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      const features = stored ? JSON.parse(stored) : {};
      features[key] = value;
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(features));
      window.location.reload();
    } catch (e) {
      log.error('Error setting feature toggle in localStorage', e);
    }
  };
}

export const useFeatureToggle = () => {
  const { data: featureFlags, isLoading, error } = useGetFeatureToggleQuery();

  const hasFeature = useCallback(
    (key: string): boolean => {
      // Check if the key exists in the FeatureToggle enum
      const isValidFeature = Object.values(FeatureToggle).includes(
        key as FeatureToggle
      );

      if (isValidFeature && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
          if (stored) {
            const localFeatures = JSON.parse(stored);
            // Check if the specific key exists in our local overrides
            if (Object.prototype.hasOwnProperty.call(localFeatures, key)) {
              return !!localFeatures[key];
            }
          }
        } catch (e) {
          log.error('Error parsing feature toggles from localStorage', e);
        }
      }

      if (!featureFlags) {
        return false;
      }
      return !!featureFlags[key];
    },
    [featureFlags]
  );

  return {
    hasFeature,
    featureFlags,
    isLoading,
    error,
  };
};
